import type { ReactNode } from 'react'

import { TickleLightDesktopIcon } from '@twistopayments/design-system/icons/homepage'
import { isNil } from 'ramda'

import type { HeroSliderComponent } from '@twisto/api-cms'

import { Button, StrapiMedia } from 'homepage/components/atoms'
import { PrimaryDownloadWidget, Promo } from 'homepage/components/molecules'
import { useUrlWithParams } from 'homepage/libs/hooks'

import { useHeroContext } from '../../hero-provider'
import { HeroTitle } from '../../hero-title'

import * as styles from './slider.styles'

type SliderProps = {
  isActive: boolean
  isInner?: boolean
  imagePriority?: boolean
  children?: ReactNode
}

export const Slider = ({
  title,
  highlighted,
  description,
  hasGradient = true,
  bulletList,
  isActive,
  cta,
  additionalButton,
  backgroundImage,
  isInner = false,
  imagePriority = false,
  hasQrCode,
  hasPromo,
  children,
}: HeroSliderComponent & SliderProps) => {
  const { download, promo } = useHeroContext()
  const ctaUrl = useUrlWithParams(cta?.link, cta?.includeUrlParams)
  const additionalButtonUrl = useUrlWithParams(
    additionalButton?.link,
    additionalButton?.includeUrlParams
  )

  if (children) {
    return (
      <div
        css={[
          styles.slider,
          isActive ? styles.active : '',
          isInner ? styles.inner : '',
        ]}
      >
        {children}
      </div>
    )
  }

  return (
    <div
      css={[
        styles.slider,
        isActive ? styles.active : '',
        isInner ? styles.inner : '',
      ]}
    >
      <div css={styles.content}>
        <div css={styles.hero}>
          <div css={styles.heroCell}>
            <div css={styles.cellContent}>
              {title && <HeroTitle highlighted={highlighted} title={title} />}
              {description && (
                <p css={styles.carouselDescription}>{description}</p>
              )}
              {bulletList && (
                <ul css={styles.carouselList}>
                  {bulletList.map(
                    (item) =>
                      item.text && (
                        <li key={item.id}>
                          <TickleLightDesktopIcon />
                          {item.text}
                        </li>
                      )
                  )}
                </ul>
              )}

              <div css={styles.cta}>
                {cta?.link && cta.text && (
                  <Button
                    key={cta.id}
                    text={cta.text}
                    url={ctaUrl}
                    variant="inverted"
                  />
                )}
                {additionalButton?.link && additionalButton.text && (
                  <Button
                    key={additionalButton.id}
                    text={additionalButton.text}
                    url={additionalButtonUrl}
                    variant="outlinedInverted"
                  />
                )}
              </div>

              {hasQrCode && download && (
                <PrimaryDownloadWidget
                  isExtendedAllowed={false}
                  {...download}
                />
              )}
              {hasPromo && promo && <Promo {...promo} />}
            </div>
          </div>
          <figure css={styles.figure}>
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
            <div css={styles.background(hasGradient ?? true)}>
              {!isNil(backgroundImage?.url) && (
                <StrapiMedia {...backgroundImage} priority={imagePriority} />
              )}
            </div>
          </figure>
        </div>
      </div>
    </div>
  )
}
